import React, { useMemo, useState } from 'react';
import { Button, Card, Form, Input, Space, Steps, Typography } from 'antd';
import { ArrowLeftOutlined, KeyOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { ApiConfig } from '../../services/ApiConfigs';
import { ApiServices } from '../../services/apiServices';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { routes } from '../../utils/routes.config';

const { Title, Text } = Typography;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const steps = useMemo(() => ([
    { title: 'Email' },
    { title: 'OTP' },
    { title: 'New password' },
  ]), []);

  const requestOtp = async (values) => {
    setLoading(true);
    try {
      const e = String(values.email || '').trim().toLowerCase();
      setEmail(e);
      const res = await ApiServices('post', ApiConfig.FORGOT_PASSWORD_REQUEST, { email: e });
      if (res?.success) {
        successMsg(res?.message || 'OTP sent');
        setStep(1);
      } else {
        errorMsg(res?.message || 'Failed to send OTP');
      }
    } catch (err) {
      errorMsg(err?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (values) => {
    setLoading(true);
    try {
      const res = await ApiServices('post', ApiConfig.FORGOT_PASSWORD_RESET, {
        email,
        otp: values.otp,
        password: values.password,
      });
      if (res?.success) {
        successMsg(res?.message || 'Password reset');
        navigate(routes.LOGIN);
      } else {
        errorMsg(res?.message || 'Failed to reset password');
      }
    } catch (err) {
      errorMsg(err?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-background-pattern"></div>
        <div className="login-background-gradient"></div>
      </div>

      <Link to={routes.LOGIN} className="back-to-home">
        <ArrowLeftOutlined /> Back to Login
      </Link>

      <div className="login-container">
        <Card className="login-card">
          <div className="login-header">
            <div className="login-logo-wrapper">
              <SafetyOutlined className="login-logo-icon" />
            </div>
            <Title level={2} className="login-title">Forgot Password</Title>
            <Text className="login-subtitle">Reset your password using OTP sent to email</Text>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Steps current={step} items={steps} size="small" />
          </div>

          {step === 0 && (
            <Form layout="vertical" onFinish={requestOtp} size="large" initialValues={{ email }}>
              <Form.Item
                name="email"
                label={<Text strong>Email Address</Text>}
                rules={[{ required: true }, { type: 'email' }]}
              >
                <Input prefix={<MailOutlined style={{ color: '#1890ff' }} />} placeholder="Enter your email" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block icon={<KeyOutlined />}>
                Send OTP
              </Button>
            </Form>
          )}

          {step === 1 && (
            <Form
              layout="vertical"
              onFinish={(values) => { setStep(2); }}
              size="large"
              initialValues={{ otp: '' }}
            >
              <Form.Item label={<Text strong>Email</Text>}>
                <Input value={email} disabled />
              </Form.Item>
              <Form.Item name="otp" label={<Text strong>OTP</Text>} rules={[{ required: true, len: 6 }]}>
                <Input prefix={<KeyOutlined style={{ color: '#1890ff' }} />} placeholder="6-digit OTP" maxLength={6} />
              </Form.Item>
              <Space style={{ width: '100%' }} direction="vertical" size={10}>
                <Button type="primary" htmlType="submit" block icon={<ArrowLeftOutlined rotate={180} />}>
                  Verify OTP
                </Button>
                <Button onClick={() => setStep(0)} block>
                  Change email
                </Button>
              </Space>
            </Form>
          )}

          {step === 2 && (
            <Form layout="vertical" onFinish={resetPassword} size="large" initialValues={{ otp: '', password: '' }}>
              <Form.Item label={<Text strong>Email</Text>}>
                <Input value={email} disabled />
              </Form.Item>
              <Form.Item name="otp" label={<Text strong>OTP</Text>} rules={[{ required: true, len: 6 }]}>
                <Input prefix={<KeyOutlined style={{ color: '#1890ff' }} />} placeholder="6-digit OTP" maxLength={6} />
              </Form.Item>
              <Form.Item name="password" label={<Text strong>New Password</Text>} rules={[{ required: true }, { min: 6 }]}>
                <Input.Password prefix={<LockOutlined style={{ color: '#1890ff' }} />} placeholder="New password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block icon={<LockOutlined />}>
                Reset Password
              </Button>
            </Form>
          )}
        </Card>
      </div>
    </div>
  );
}

